import React, { useState } from 'react';
import { DEMONOLATRY_DATA } from './archiveData';

export const DemonolatryRites: React.FC = () => {
  const [targetCadence, setTargetCadence] = useState<number>(9);
  const [currentCount, setCurrentCount] = useState<number>(0);
  const [activePhaseIndex, setActivePhaseIndex] = useState<number>(0);
  const [copiedPhase, setCopiedPhase] = useState<boolean>(false);

  const handleIncrement = () => {
    if (currentCount < targetCadence) {
      setCurrentCount(prev => prev + 1);
    }
  };

  const handleReset = () => {
    setCurrentCount(0);
  };

  const handleCopyScript = async (text: string) => {
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(text);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopiedPhase(true);
      setTimeout(() => setCopiedPhase(false), 2000);
    } catch {
      // Fallback
    }
  };

  const activePhase = DEMONOLATRY_DATA.riteOfUnyokedFoundation.phases[activePhaseIndex];

  return (
    <div className="space-y-10 animate-fade-in" id="demonolatry-rites-section">
      <div className="border-b border-iron-dark pb-4">
        <div className="flex items-center gap-2 text-xs font-mono text-ember-spark uppercase tracking-widest">
          <span>LIBER QUARTUS</span>
          <span>•</span>
          <span>PRACTICAL DEMONOLATRY & RITUAL GRIMOIRE</span>
        </div>
        <h2 className="font-display text-2xl sm:text-3xl text-parchment-light uppercase mt-1">
          Demonolatry Rites, Offerings & Dukante Enn
        </h2>
        <p className="text-xs sm:text-sm text-parchment-dim mt-2 max-w-3xl">
          Operative vocal keys, elemental earth alignments, altar geometry, and the canonical Rite of the Unyoked Foundation.
        </p>
      </div>

      {/* The Dukante Enn & Pronunciation Guide */}
      <div className="chthonic-card rounded-lg p-6 sm:p-8 lg:p-10 space-y-6">
        <div className="border-b border-iron-dark pb-4">
          <span className="text-[10px] font-mono text-gold-ancient uppercase tracking-widest">
            CLAVIS VOCALIS • CHYTHONIC SONIC KEY
          </span>
          <h3 className="font-display text-2xl sm:text-3xl font-bold text-gold-bright mt-1">
            {DEMONOLATRY_DATA.enn.canonical}
          </h3>
          <div className="flex flex-wrap gap-4 text-xs font-mono text-iron-steel mt-2">
            <span>IPA: <strong className="text-parchment-light">{DEMONOLATRY_DATA.enn.ipa}</strong></span>
            <span>•</span>
            <span>English: <strong className="text-parchment-light">{DEMONOLATRY_DATA.enn.phonetic}</strong></span>
          </div>
          <p className="text-xs text-parchment-dim/80 font-serif italic mt-2">
            {DEMONOLATRY_DATA.enn.vibrationalGuidance}
          </p>
        </div>

        {/* Syllable Breakdown */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {DEMONOLATRY_DATA.enn.syllables.map((syl) => (
            <div key={syl.term} className="rounded border border-iron-dark bg-obsidian-deep/80 p-3 space-y-1">
              <div className="font-display text-sm font-bold text-gold-bright">{syl.term}</div>
              <div className="text-[10px] font-mono text-iron-steel">{syl.approx}</div>
              <p className="text-[11px] text-parchment-dim leading-snug">{syl.esoteric}</p>
            </div>
          ))}
        </div>

        {/* Interactive Repetition Counter / Cadence Tracker */}
        <div className="rounded-lg border border-iron-dark/80 bg-obsidian-deep/90 p-5 sm:p-6 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <span className="text-[10px] font-mono text-gold-ancient uppercase tracking-widest block">
                CADENCE TRACKER
              </span>
              <span className="text-xs font-serif text-parchment-dim">
                Select target vibratory cadence and count repetitions during ritual chanting.
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {DEMONOLATRY_DATA.enn.cadences.map((c) => (
                <button
                  key={c.count}
                  onClick={() => {
                    setTargetCadence(c.count);
                    setCurrentCount(0);
                  }}
                  className={`px-3 py-1 text-xs font-mono rounded transition-colors ${
                    targetCadence === c.count
                      ? 'bg-gold-ancient text-obsidian-deep font-bold'
                      : 'border border-iron-base bg-obsidian-surface text-parchment-dim hover:text-parchment-light'
                  }`}
                >
                  {c.count}x
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-6 pt-2">
            {/* Counter display */}
            <div className="text-center sm:text-left min-w-[120px]">
              <div className="text-3xl sm:text-4xl font-display font-bold text-gold-bright">
                {currentCount} <span className="text-sm font-mono text-iron-steel font-normal">/ {targetCadence}</span>
              </div>
              <div className="text-[10px] font-mono text-parchment-dim uppercase mt-1">
                {currentCount >= targetCadence ? 'CADENCE COMPLETE' : 'IN VIBRATION'}
              </div>
            </div>

            {/* Progress Bar */}
            <div className="flex-1 w-full bg-obsidian-surface rounded-full h-3 border border-iron-dark overflow-hidden">
              <div
                className="bg-gradient-to-r from-bronze-dark via-gold-ancient to-gold-bright h-full transition-all duration-300 shadow-glow-gold"
                style={{ width: `${Math.min(100, (currentCount / targetCadence) * 100)}%` }}
              />
            </div>

            {/* Controls */}
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <button
                onClick={handleIncrement}
                disabled={currentCount >= targetCadence}
                className="btn-gold flex-1 sm:flex-none px-5 py-2 text-xs font-semibold rounded disabled:opacity-50"
              >
                Vibrate (+1)
              </button>
              <button
                onClick={handleReset}
                className="rounded border border-iron-base bg-obsidian-surface px-4 py-2 text-xs font-mono text-parchment-dim hover:text-parchment-light transition-colors"
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Correspondences & Offerings Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Correspondences */}
        <div className="chthonic-card rounded-lg p-6 sm:p-8 space-y-4">
          <span className="text-[10px] font-mono text-gold-ancient uppercase tracking-widest">
            CORRESPONDENTIA CHTHONIA
          </span>
          <h3 className="font-display text-lg text-parchment-light">
            Altar Correspondences & Temporal Hours
          </h3>
          <div className="divide-y divide-iron-dark/60 text-xs">
            {DEMONOLATRY_DATA.correspondences.map((c) => (
              <div key={c.label} className="py-2.5 flex justify-between gap-4">
                <span className="font-mono text-iron-steel">{c.label}</span>
                <span className="text-right text-parchment-light font-serif">{c.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Offerings & Incense */}
        <div className="chthonic-card rounded-lg p-6 sm:p-8 space-y-4">
          <span className="text-[10px] font-mono text-ember-spark uppercase tracking-widest">
            SACRIFICIA ET THYMIAMA
          </span>
          <h3 className="font-display text-lg text-parchment-light">
            Incense Formulation & Libations
          </h3>

          <div className="space-y-4 text-xs font-serif">
            <div>
              <h4 className="font-display text-xs text-gold-bright uppercase tracking-wider mb-2">
                "The Soil of the Grave" Incense Recipe
              </h4>
              <ul className="space-y-1 text-parchment-dim">
                {DEMONOLATRY_DATA.offerings.incenseRecipe.map((ing, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-gold-ancient font-mono">•</span>
                    <span>{ing}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="pt-2 border-t border-iron-dark/60">
              <h4 className="font-display text-xs text-gold-bright uppercase tracking-wider mb-2">
                Traditional Libations
              </h4>
              <ul className="space-y-1 text-parchment-dim">
                {DEMONOLATRY_DATA.offerings.libations.map((lib, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-ember-spark font-mono">•</span>
                    <span>{lib}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Operative Ritual Script: Rite of the Unyoked Foundation */}
      <div className="chthonic-card rounded-lg p-6 sm:p-8 lg:p-10 space-y-6">
        <div className="border-b border-iron-dark pb-4">
          <span className="text-[10px] font-mono text-gold-ancient uppercase tracking-widest">
            ORDO CEREMONIALIS • RITUS FUNDAMENTI INDOMITI
          </span>
          <h3 className="font-display text-2xl text-parchment-light mt-1">
            {DEMONOLATRY_DATA.riteOfUnyokedFoundation.name}
          </h3>
          <p className="text-xs text-parchment-dim mt-2 font-serif italic">
            {DEMONOLATRY_DATA.riteOfUnyokedFoundation.preparation}
          </p>
        </div>

        {/* Phase Selectors */}
        <div className="flex flex-wrap gap-2" role="tablist" aria-label="Ritual Phases">
          {DEMONOLATRY_DATA.riteOfUnyokedFoundation.phases.map((ph, idx) => (
            <button
              key={ph.phase}
              role="tab"
              aria-selected={activePhaseIndex === idx}
              onClick={() => {
                setActivePhaseIndex(idx);
                setCopiedPhase(false);
              }}
              className={`px-3.5 py-2 rounded text-xs font-mono uppercase transition-colors ${
                activePhaseIndex === idx
                  ? 'bg-gold-bright text-obsidian-deep font-bold'
                  : 'border border-iron-dark bg-obsidian-deep text-parchment-dim hover:text-parchment-light'
              }`}
            >
              {ph.phase}: {ph.name}
            </button>
          ))}
        </div>

        {/* Active Phase Display */}
        <div className="rounded-lg border border-bronze-dark/60 bg-obsidian-deep/90 p-5 sm:p-7 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-iron-dark/60 pb-3">
            <div>
              <span className="text-[10px] font-mono text-gold-ancient uppercase tracking-wider block">
                {activePhase.phase}
              </span>
              <h4 className="font-display text-lg text-parchment-light">
                {activePhase.name}
              </h4>
            </div>
            <button
              onClick={() => handleCopyScript(`${activePhase.name}\n\nRubric: ${activePhase.rubric}\n\nSpoken:\n${activePhase.spoken}`)}
              className="rounded border border-iron-base bg-obsidian-surface px-3 py-1.5 text-xs font-mono text-gold-ancient hover:border-gold-bright hover:text-gold-bright transition-colors self-start sm:self-auto"
            >
              {copiedPhase ? 'SCRIPT COPIED' : 'COPY PHASE SCRIPT'}
            </button>
          </div>

          <div className="rounded border border-iron-dark/60 bg-obsidian-surface/60 p-3 text-xs font-serif italic text-gold-bright/90">
            <strong className="font-mono text-[10px] text-ember-spark not-italic uppercase block mb-1">Ritual Rubric:</strong>
            {activePhase.rubric}
          </div>

          <div className="space-y-2">
            <span className="font-mono text-[10px] text-iron-steel uppercase block">Spoken Liturgy:</span>
            <pre className="whitespace-pre-wrap font-serif text-xs sm:text-sm text-parchment-light leading-relaxed pt-1">
              {activePhase.spoken}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemonolatryRites;
